import { CarlineData, FeatureAppTrackingManager } from '../typings/tagging';
import { cleanPlaintext } from '../components/Guide/Content/Search/helpers';
import { getDefaultTrackingEvent } from '../hooks/use-tracking-manager';
import {
  SectionModel,
  VehicleModel,
  NavigationSection,
  QsgSearchResult,
} from '../fetch-data/graphql/types';

const modelDict: Record<string, CarlineData> = {
  atlas: {
    CarlineId: '31691',
    CarlineName: 'Atlas',
    MetadataName: 'Atlas',
  },
  'atlas-cross-sport': {
    CarlineId: '31692',
    CarlineName: 'Atlas Cross Sport',
    MetadataName: 'Atlas Cross Sport',
  },
  'golf-gti-golf-r': {
    CarlineId: '30310',
    CarlineName: 'Golf R',
    MetadataName: 'Golf GTI/Golf R',
  },
  jetta: {
    CarlineId: '30906',
    CarlineName: 'Jetta',
    MetadataName: 'Jetta',
  },
  taos: {
    CarlineId: '30385',
    CarlineName: 'Taos',
    MetadataName: 'Taos',
  },
  tiguan: {
    CarlineId: '31150',
    CarlineName: 'Tiguan',
    MetadataName: 'Tiguan',
  },
  'id-buzz': {
    CarlineId: '72006',
    CarlineName: 'ID. Buzz',
    MetadataName: 'ID. Buzz',
  },
  'id-4': {
    CarlineId: '30291',
    CarlineName: 'ID.4',
    MetadataName: 'ID.4',
  },
  'id-4-limited': {
    CarlineId: '30291',
    CarlineName: 'ID.4',
    MetadataName: 'ID.4 Limited & S Limited',
  },
  'id-4-pro': {
    CarlineId: '30291',
    CarlineName: 'ID.4',
    MetadataName: 'ID.4 Pro, Pro S, & Pro S Plus',
  },
};

export const getCarlineData = (modelId: string): CarlineData | undefined => {
  return modelDict[modelId];
};

export const onVWBasicPageLoadEvent = (
  trackingManager: FeatureAppTrackingManager
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Quick Start Guide page load`,
  };

  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_GenericFeatureApp_Load',
      customEvent
    );
};

export const onVWBasicFilterSearchButtonClickEvent_ModelSelector = (
  trackingManager: FeatureAppTrackingManager,
  { modelName, modelYear }: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Filter vehicle model selection',
  };

  customEvent.filter = {
    filterlist: [
      {
        name: 'CG',
        type: 'single_value',
        values: [modelName],
      },
      {
        name: 'MY',
        type: 'single_value',
        values: [modelYear],
      },
    ],
    name: `Model selection`,
  };

  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_FilterSearchButton_Click',
      customEvent
    );
};

export const onVWBasicFilterResultLoadEvent_ModelSelector = (
  trackingManager: FeatureAppTrackingManager,
  { modelName, modelYear }: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'QSG filter result load',
  };

  customEvent.filter = {
    filterlist: [
      {
        name: 'CG',
        type: 'single_value',
        values: [modelName],
      },
      {
        name: 'MY',
        type: 'single_value',
        values: [modelYear],
      },
    ],
    name: `QSG filter result load`,
    numberOfResults: 1,
  };

  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_FilterResult_Load',
      customEvent
    );
};

export const onVWBasicAccordionOpenClickEvent = (
  trackingManager: FeatureAppTrackingManager,
  section: SectionModel | NavigationSection,
  open: boolean,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Navigation section accordion open',
  };

  customEvent.accordion = {
    groupItemName: section.sectionName,
    groupItemState: open ? 'open' : 'closed',
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  if (open)
    trackingManager &&
      trackingManager.trackQSGGlobalEvent(
        'VWBasic_AccordionOpen_Click',
        customEvent
      );
};

export const onVWBasicLinkClickEvent_NavigationMenu = (
  trackingManager: FeatureAppTrackingManager,
  section: SectionModel | NavigationSection,
  isNewTab: boolean = false,
  href?: string,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Navigation section link click',
  };

  customEvent.link = {
    url: href,
    name: section.sectionName,
    newTab: isNewTab,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Link_Click', customEvent);
};

export const onVWBasicLinkClickEvent_OpenGuide = (
  trackingManager: FeatureAppTrackingManager,
  isNewTab: boolean = false,
  href: string,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Open Guide link click',
  };


  customEvent.link = {
    url: href,
    name: "Open Guide",
    newTab: isNewTab,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Link_Click', customEvent);
};

export const onVWBasicLinkClickEvent_CTALink = (
  trackingManager: FeatureAppTrackingManager,
  href: string,
  isNewTab: boolean = false,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'CTA link click',
  };

  customEvent.link = {
    url: href,
    name: `CTA link: ${href}`,
    newTab: isNewTab,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Link_Click', customEvent);
};

export const onVWBasicDownloadClickEvent = (
  trackingManager: FeatureAppTrackingManager,
  model: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Download vehicle model QSG PDF file click`,
  };

  customEvent.content = {
    Name: `${model.modelName} ${model.modelYear} QSG PDF file`,
    Download: true,
  };

  const carlineData = getCarlineData(model.modelId);
  if (carlineData)
    customEvent.configuration = {
      CarlineId: carlineData.CarlineId,
      CarlineName: carlineData.CarlineName,
      ModelYear: model.modelYear,
    };

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Download_Click', customEvent);
};

export const onVWBasicLinkClickEvent_SearchAutocomplete = (
  trackingManager: FeatureAppTrackingManager,
  searchTerm: string,
  href: string,
  isNewTab: boolean = false,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Search autocomplete link click',
  };

  customEvent.link = {
    url: href,
    name: `Search Term: ${cleanPlaintext(searchTerm)}`,
    newTab: isNewTab,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Link_Click', customEvent);
};

export const onVWBasicLinkClickEvent_SearchResult = (
  trackingManager: FeatureAppTrackingManager,
  searchTerm: string,
  searchResult: QsgSearchResult,
  isNewTab: boolean = false,
  resultRelativeIndex: number,
  totalResults: number,
  href: string,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  const searchPageUrl =
    model && model.modelYear && model.modelId
      ? `/${model.modelYear}/${model.modelId}?search=${encodeURIComponent(searchTerm)}`
      : `/?search=${encodeURIComponent(searchTerm)}`;

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Result #: ${resultRelativeIndex} of ${totalResults}`,
    pageId: searchPageUrl,
    pageName: `Search results page for term: ${cleanPlaintext(searchTerm)}`,
  };

  customEvent.link = {
    url: href,
    name: cleanPlaintext(searchResult.resultTitle.plaintext),
    newTab: isNewTab,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Link_Click', customEvent);
};

export const onVWBasicButtonClickEvent_PaginationButton = (
  trackingManager: FeatureAppTrackingManager,
  _: string,
  buttonId: 'Previous' | 'Next' | number,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Search page button ${buttonId}`,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent('VWBasic_Button_Click', customEvent);
};

export const onVWBasicInfoMessageLoadEvent = (
  _: FeatureAppTrackingManager,
  infoMessage: string,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Info message load`,
  };

  customEvent.error = {
    message: infoMessage,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  /* Ignore this event
  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_InfoMessage_Load',
      customEvent
    );*/
};

export const onVWBasicFilterSearchButtonClickEvent_SearchEnter = (
  trackingManager: FeatureAppTrackingManager,
  searchTerm: string,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Entered search term',
  };

  customEvent.filter = {
    filterlist: [
      {
        name: 'ST',
        type: 'single_value',
        values: [cleanPlaintext(searchTerm)],
      },
    ],
    name: `Search term entered`,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_FilterSearchButton_Click',
      customEvent
    );
};

export const onVWBasicFilterSearchResultLoadEvent = (
  trackingManager: FeatureAppTrackingManager,
  searchTerm: string,
  numberOfResults: number,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Search results loaded',
  };

  customEvent.filter = {
    filterlist: [
      {
        name: 'ST',
        type: 'single_value',
        values: [cleanPlaintext(searchTerm)],
      },
    ],
    values: numberOfResults === 0 ? ['No results'] : [],
    name: `Search results loaded`,
    numberOfResults: numberOfResults,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_FilterResult_Load',
      customEvent
    );
};

export const onVWBasicGenericErrorMessageLoadEvent = (
  trackingManager: FeatureAppTrackingManager,
  errorMessage: string,
  model?: VehicleModel
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Generic error message load`,
  };

  customEvent.error = {
    message: errorMessage,
  };

  if (model) {
    const carlineData = getCarlineData(model.modelId);
    if (carlineData)
      customEvent.configuration = {
        CarlineId: carlineData.CarlineId,
        CarlineName: carlineData.CarlineName,
        ModelYear: model.modelYear,
      };
  }

  trackingManager &&
    trackingManager.trackQSGGlobalEvent(
      'VWBasic_GenericErrorMessage_Load',
      customEvent
    );
};
